import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Plus, Calendar, Pause, Play, Trash2, BarChart3,
  CheckCircle2, Clock, AlertTriangle, Send, Loader2, Target,
  ChevronRight, LayoutGrid, RefreshCw, Settings2, MoreHorizontal
} from 'lucide-react';
import { Button } from '../ui/button';
import { ANGLE_LABELS } from '../../data/pinTemplates';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({});

const STATUS_LABELS = {
  draft: { label: 'Draft', color: 'text-stone-500 bg-stone-50', icon: Clock },
  generating: { label: 'Genererer...', color: 'text-amber-600 bg-amber-50', icon: Loader2 },
  review: { label: 'Under review', color: 'text-blue-600 bg-blue-50', icon: BarChart3 },
  scheduled: { label: 'Planlagt', color: 'text-indigo-600 bg-indigo-50', icon: Calendar },
  active: { label: 'Active', color: 'text-green-600 bg-green-50', icon: Send },
  paused: { label: 'Pauset', color: 'text-amber-600 bg-amber-50', icon: Pause },
  completed: { label: 'Completed', color: 'text-emerald-600 bg-emerald-50', icon: CheckCircle2 },
};

const GOAL_LABELS = {
  drive_traffic: 'Drive trafikk',
  promote_product: 'Promoter product',
  build_awareness: 'Bygg visiblehet',
  get_saves: 'Get saves',
  educational_growth: 'Edukativt innhold',
};

const CampaignDashboard = ({ campaignId, onBack, onEditCampaign, onEditPin, onAddPins }) => {
  const [campaign, setCampaign] = useState(null);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pausing, setPausing] = useState(false);

  const fetchCampaign = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/campaigns/${campaignId}`, getAuth());
      setCampaign(res.data);
      setPins(res.data.pins || []);
    } catch (e) {
      console.error('Fetch campaign error:', e);
    }
    setLoading(false);
  }, [campaignId]);

  useEffect(() => { fetchCampaign(); }, [fetchCampaign]);

  const handlePause = async () => {
    setPausing(true);
    try {
      await axios.post(`${API}/api/campaigns/${campaignId}/pause`, {}, getAuth());
      await fetchCampaign();
    } catch (e) {
      console.error('Pause error:', e);
    }
    setPausing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this campaign and all associated pins?')) return;
    try {
      await axios.delete(`${API}/api/campaigns/${campaignId}`, getAuth());
      onBack();
    } catch (e) {
      console.error('Delete error:', e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#B49A88]" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-20">
        <p className="font-sans text-sm text-stone-500">Campaign not found</p>
        <Button variant="outline" onClick={onBack} className="mt-4 rounded-xl text-xs font-sans font-semibold">Back</Button>
      </div>
    );
  }

  const stats = campaign.stats || {};
  const statusInfo = STATUS_LABELS[campaign.status] || STATUS_LABELS.draft;
  const StatusIcon = statusInfo.icon;
  const totalPins = pins.length;
  const progressPct = totalPins > 0 ? Math.round(((stats.published || 0) / totalPins) * 100) : 0;

  // Timeline: scheduled dates
  const scheduledPins = pins.filter(p => p.scheduled_at).sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  const dateGroups = {};
  scheduledPins.forEach(p => {
    const day = p.scheduled_at?.split('T')[0];
    if (!dateGroups[day]) dateGroups[day] = [];
    dateGroups[day].push(p);
  });

  return (
    <div className="space-y-6" data-testid="campaign-dashboard">
      {/* Header */}
      <div>
        <button onClick={onBack} className="flex items-center gap-1.5 text-stone-400 hover:text-stone-600 font-sans text-xs font-medium mb-3 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> All kampanjer
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-serif font-bold text-stone-800 text-xl">{campaign.name}</h2>
            <div className="flex items-center gap-3 mt-1.5">
              <span className={`inline-flex items-center gap-1.5 text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full ${statusInfo.color}`}>
                <StatusIcon className="w-3 h-3" /> {statusInfo.label}
              </span>
              {campaign.goal && (
                <span className="text-[10px] font-sans text-stone-400">
                  <Target className="w-3 h-3 inline mr-0.5" /> {GOAL_LABELS[campaign.goal] || campaign.goal}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(campaign.status === 'scheduled' || campaign.status === 'paused') && (
              <Button
                variant="outline"
                onClick={handlePause}
                disabled={pausing}
                className="rounded-xl px-4 py-2.5 text-xs font-sans font-semibold"
                data-testid="pause-campaign-btn"
              >
                {campaign.status === 'paused' ? <><Play className="w-3.5 h-3.5 mr-1.5" /> Gjenoppta</> : <><Pause className="w-3.5 h-3.5 mr-1.5" /> Pause</>}
              </Button>
            )}
            {campaign.status === 'review' && (
              <Button
                onClick={() => onEditCampaign(campaignId)}
                className="bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl px-4 py-2.5 text-xs font-sans font-semibold"
                data-testid="review-campaign-btn"
              >
                <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Review pins
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: 'Totalt', value: totalPins, color: 'bg-stone-50 text-stone-600' },
          { label: 'Trenger review', value: stats.needs_review || 0, color: 'bg-amber-50 text-amber-600' },
          { label: 'Godkjent', value: stats.approved || 0, color: 'bg-blue-50 text-blue-600' },
          { label: 'Planlagt', value: stats.scheduled || 0, color: 'bg-indigo-50 text-indigo-600' },
          { label: 'Published', value: stats.published || 0, color: 'bg-green-50 text-green-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-stone-100 p-4" data-testid={`campaign-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <p className="font-sans text-2xl font-bold text-stone-800">{s.value}</p>
            <p className="font-sans text-[10px] text-stone-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      {totalPins > 0 && (
        <div className="bg-white rounded-xl border border-stone-100 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-sans text-xs font-semibold text-stone-700">Fremdrift</p>
            <p className="font-sans text-xs text-stone-400">{progressPct}%</p>
          </div>
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#E60023] rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-[9px] font-sans text-stone-400">
              <div className="w-2 h-2 rounded-full bg-[#E60023]" /> Published ({stats.published || 0})
            </span>
            <span className="flex items-center gap-1 text-[9px] font-sans text-stone-400">
              <div className="w-2 h-2 rounded-full bg-indigo-400" /> Planlagt ({stats.scheduled || 0})
            </span>
            <span className="flex items-center gap-1 text-[9px] font-sans text-stone-400">
              <div className="w-2 h-2 rounded-full bg-amber-400" /> Trenger review ({stats.needs_review || 0})
            </span>
          </div>
        </div>
      )}

      {/* Campaign dates */}
      {(campaign.start_date || campaign.end_date) && (
        <div className="bg-white rounded-xl border border-stone-100 p-4">
          <p className="font-sans text-xs font-semibold text-stone-700 mb-2">Kampanjeperiode</p>
          <div className="flex items-center gap-2 text-xs font-sans text-stone-500">
            <Calendar className="w-3.5 h-3.5 text-stone-400" />
            <span>
              {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set'}
            </span>
            <span className="text-stone-300">→</span>
            <span>
              {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set'}
            </span>
          </div>
        </div>
      )}

      {/* Schedule timeline */}
      {Object.keys(dateGroups).length > 0 && (
        <div className="bg-white rounded-xl border border-stone-100 p-5">
          <p className="font-sans text-xs font-semibold text-stone-700 mb-4">Publishing plan</p>
          <div className="space-y-4">
            {Object.entries(dateGroups).slice(0, 7).map(([day, dayPins]) => (
              <div key={day} className="flex items-start gap-3">
                <div className="w-16 flex-shrink-0 text-right">
                  <p className="font-sans text-[11px] font-semibold text-stone-700">
                    {new Date(day).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                  </p>
                  <p className="font-sans text-[9px] text-stone-400">{dayPins.length} pins</p>
                </div>
                <div className="w-px bg-stone-200 self-stretch min-h-[20px]" />
                <div className="flex-1 flex flex-wrap gap-1.5">
                  {dayPins.map(p => {
                    const aInfo = ANGLE_LABELS[p.marketing_angle] || { label: p.marketing_angle, color: '#888' };
                    return (
                      <button
                        key={p.id}
                        onClick={() => onEditPin(p.id)}
                        className="px-2.5 py-1 rounded-lg border border-stone-100 hover:border-stone-200 bg-white text-[9px] font-sans text-stone-600 hover:shadow-sm transition-all flex items-center gap-1"
                      >
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: aInfo.color }} />
                        {p.headline?.slice(0, 25)}...
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Products in campaign */}
      <div className="bg-white rounded-xl border border-stone-100 p-5">
        <p className="font-sans text-xs font-semibold text-stone-700 mb-3">Products i kampanjen</p>
        <div className="space-y-2">
          {(campaign.products || []).map((p, i) => {
            const productPins = pins.filter(pin => pin.source_product_title === p.title);
            return (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-stone-50/50">
                <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center text-[10px] font-bold text-stone-400 flex-shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans font-medium text-stone-700 text-xs truncate">{p.title}</p>
                  {p.category && <p className="font-sans text-[9px] text-stone-400">{p.category}</p>}
                </div>
                <span className="text-[9px] font-sans font-semibold text-stone-400">{productPins.length} pins</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Danger zone */}
      <div className="flex items-center justify-end gap-2 pt-4 border-t border-stone-100">
        <button
          onClick={handleDelete}
          className="text-[10px] font-sans font-medium text-red-400 hover:text-red-600 transition-colors"
          data-testid="delete-campaign-btn"
        >
          <Trash2 className="w-3 h-3 inline mr-1" /> Delete campaign
        </button>
      </div>
    </div>
  );
};

export default CampaignDashboard;
